// This file is hand-maintained. Do NOT regenerate it with
// `./bin/rails stimulus:manifest:update` — that command rewrites the file
// with eager static imports and would clobber the lazyLoadController()
// registrations below. When you add a new Stimulus controller, decide
// whether it belongs in the "always loaded" section (truly global, used
// by the layout / shared partials, or tiny utilities sprinkled across
// the app) or the "lazy-loaded" section (page- or feature-specific) and
// add it by hand following the surrounding pattern.

import { application } from "./application"
import { lazyLoadController } from "../lib/lazy_controller_loader"

// ---------------------------------------------------------------------------
// Always-loaded controllers
//
// These ship in the main application.js bundle because they are either used
// by the layout / shared partials (so they need to be registered before the
// initial paint) or they are tiny utilities used across so many pages that
// an extra HTTP round-trip per page would cost more than it saves.
// ---------------------------------------------------------------------------

import AffectRecordController from "./affect_record_controller"
application.register("affect-record", AffectRecordController)

import ApplicationController from "./application_controller"
application.register("application", ApplicationController)

import Bs__PopoverController from "./bs/popover_controller"
application.register("bs--popover", Bs__PopoverController)

import Bs__TooltipController from "./bs/tooltip_controller"
application.register("bs--tooltip", Bs__TooltipController)

import ClearDatesController from "./clear_dates_controller"
application.register("clear-dates", ClearDatesController)

import ClipboardController from "./clipboard_controller"
application.register("clipboard", ClipboardController)

import CollapseGroupsController from "./collapse_groups_controller"
application.register("collapse-groups", CollapseGroupsController)

import ConfirmationController from "./confirmation_controller"
application.register("confirmation", ConfirmationController)

import ContentLoaderController from "./content_loader_controller"
application.register("content-loader", ContentLoaderController)

import CounterController from "./counter_controller"
application.register("counter", CounterController)

import CountersController from "./counters_controller"
application.register("counters", CountersController)

import DynamicSelectController from "./dynamic_select_controller"
application.register("dynamic-select", DynamicSelectController)

import FilterController from "./filter_controller"
application.register("filter", FilterController)

import FormSubmitController from "./form_submit_controller"
application.register("form-submit", FormSubmitController)

import IoParamNameController from "./io_param_name_controller"
application.register("io-param-name", IoParamNameController)

import JsUtilityController from "./js_utility_controller"
application.register("js-utility", JsUtilityController)

import ListFilterController from "./list_filter_controller"
application.register("list-filter", ListFilterController)

import LoadValueController from "./load_value_controller"
application.register("load-value", LoadValueController)

import NestedFormController from "./nested_form_controller"
application.register("nested-form", NestedFormController)

import PageActionsBarController from "./page_actions_bar_controller"
application.register("page-actions-bar", PageActionsBarController)

import PreferenceToggleController from "./preference_toggle_controller"
application.register("preference-toggle", PreferenceToggleController)

import RemoteFormCancelController from "./remote_form_cancel_controller"
application.register("remote-form-cancel", RemoteFormCancelController)

import RemoteModalController from "./remote_modal_controller"
application.register("remote-modal", RemoteModalController)

import ResetFormController from "./reset_form_controller"
application.register("reset-form", ResetFormController)

import SelectInputController from "./select_input_controller"
application.register("select-input", SelectInputController)

import SubmitSpinnerController from "./submit_spinner_controller"
application.register("submit-spinner", SubmitSpinnerController)

import ToggleController from "./toggle_controller"
application.register("toggle", ToggleController)

import UpdateInPlaceController from "./update_in_place_controller"
application.register("update-in-place", UpdateInPlaceController)

// ---------------------------------------------------------------------------
// Lazy-loaded controllers
//
// Each entry registers a placeholder that defers the import until a matching
// `data-controller="..."` is found in the DOM, then registers the real
// controller (re-checked on every `turbo:load`). The implementation file
// and its transitive deps are emitted as a separate chunk under
// app/assets/builds/chunks/ and only fetched when the page needs it.
// ---------------------------------------------------------------------------

lazyLoadController(application, "activity-logs", () => import("./activity_logs_controller"))
lazyLoadController(application, "assignments", () => import("./assignments_controller"))
lazyLoadController(application, "associated-log-times", () => import("./associated_log_times_controller"))
lazyLoadController(application, "candidates", () => import("./candidates_controller"))
lazyLoadController(application, "charges", () => import("./charges_controller"))
lazyLoadController(application, "clone-pilot-time", () => import("./clone_pilot_time_controller"))
lazyLoadController(application, "comments", () => import("./comments_controller"))
lazyLoadController(application, "communications", () => import("./communications_controller"))
lazyLoadController(application, "create-day-off-log", () => import("./create_day_off_log_controller"))
lazyLoadController(application, "crew-schedule-drag", () => import("./crew_schedule_drag_controller"))
lazyLoadController(application, "day-rate-lookup", () => import("./day_rate_lookup_controller"))
lazyLoadController(application, "doc-years", () => import("./doc_years_controller"))
lazyLoadController(application, "dropzone", () => import("./dropzone_controller"))
lazyLoadController(application, "dual-scrollbar", () => import("./dual_scrollbar_controller"))
lazyLoadController(application, "editor", () => import("./editor_controller"))
lazyLoadController(application, "equipment-data-tag-parser", () => import("./equipment_data_tag_parser_controller"))
lazyLoadController(application, "equipment-ops-drag", () => import("./equipment_ops_drag_controller"))
lazyLoadController(application, "expenses", () => import("./expenses_controller"))
lazyLoadController(application, "extended-time", () => import("./extended_time_controller"))
lazyLoadController(application, "feature-card-autosave", () => import("./feature_card_autosave_controller"))
lazyLoadController(application, "feature-showcase", () => import("./feature_showcase_controller"))
lazyLoadController(application, "file-size-validator", () => import("./file_size_validator_controller"))
lazyLoadController(application, "filter-expenses", () => import("./filter_expenses_controller"))
lazyLoadController(application, "filter-logs", () => import("./filter_logs_controller"))
lazyLoadController(application, "filter-schedule", () => import("./filter_schedule_controller"))
lazyLoadController(application, "frequencies", () => import("./frequencies_controller"))
lazyLoadController(application, "geolocation", () => import("./geolocation_controller"))
lazyLoadController(application, "hobbs-inputs", () => import("./hobbs_inputs_controller"))
lazyLoadController(application, "image-drop-zone", () => import("./image_drop_zone_controller"))
lazyLoadController(application, "inline-edit", () => import("./inline_edit_controller"))
lazyLoadController(application, "inspection-carousel", () => import("./inspection_carousel_controller"))
lazyLoadController(application, "invoice-hobbs", () => import("./invoice_hobbs_controller"))
lazyLoadController(application, "itineraries", () => import("./itineraries_controller"))
lazyLoadController(application, "keyword-context-modal", () => import("./keyword_context_modal_controller"))
lazyLoadController(application, "location", () => import("./location_controller"))
lazyLoadController(application, "location-lookup", () => import("./location_lookup_controller"))
lazyLoadController(application, "mitigation-selector", () => import("./mitigation_selector_controller"))
lazyLoadController(application, "new-user-username", () => import("./new_user_username_controller"))
lazyLoadController(application, "nfc-scan", () => import("./nfc_scan_controller"))
lazyLoadController(application, "part-created-handler", () => import("./part_created_handler_controller"))
lazyLoadController(application, "part-request-form", () => import("./part_request_form_controller"))
lazyLoadController(application, "part-row", () => import("./part_row_controller"))
lazyLoadController(application, "payroll-approvals", () => import("./payroll_approvals_controller"))
lazyLoadController(application, "payroll-assignment-autosave", () => import("./payroll_assignment_autosave_controller"))
lazyLoadController(application, "payroll-group-toggle", () => import("./payroll_group_toggle_controller"))
lazyLoadController(application, "payroll-row-toggle", () => import("./payroll_row_toggle_controller"))
lazyLoadController(application, "payroll-summary", () => import("./payroll_summary_controller"))
lazyLoadController(application, "periodic-deferral-reason", () => import("./periodic_deferral_reason_controller"))
lazyLoadController(application, "pilot-role-toggle", () => import("./pilot_role_toggle_controller"))
lazyLoadController(application, "places", () => import("./places_controller"))
lazyLoadController(application, "preview-mitigation", () => import("./preview_mitigation_controller"))
lazyLoadController(application, "preview-text-score", () => import("./preview_text_score_controller"))
lazyLoadController(application, "project-actions", () => import("./project_actions_controller"))
lazyLoadController(application, "rate-status-preview", () => import("./rate_status_preview_controller"))
lazyLoadController(application, "record-type-expiration-mode", () => import("./record_type_expiration_mode_controller"))
lazyLoadController(application, "requirement-selector", () => import("./requirement_selector_controller"))
lazyLoadController(application, "risk--ra-mitigations", () => import("./risk/ra_mitigations_controller"))
lazyLoadController(application, "risk--ra-risks", () => import("./risk/ra_risks_controller"))
lazyLoadController(application, "service-item-schedule", () => import("./service_item_schedule_controller"))
lazyLoadController(application, "service-records", () => import("./service_records_controller"))
lazyLoadController(application, "onboarding-spreadsheet-sortable", () => import("./onboarding_spreadsheet_sortable_controller"))
lazyLoadController(application, "sortable", () => import("./sortable_controller"))
lazyLoadController(application, "spreadsheet-row-toggle", () => import("./spreadsheet_row_toggle_controller"))
lazyLoadController(application, "tidbits", () => import("./tidbits_controller"))
lazyLoadController(application, "time-error-form", () => import("./time_error_form_controller"))
lazyLoadController(application, "toggle-visibility", () => import("./toggle_visibility_controller"))
lazyLoadController(application, "training-event-category", () => import("./training_event_category_controller"))
lazyLoadController(application, "training-record-date-calculator", () => import("./training_record_date_calculator_controller"))
lazyLoadController(application, "training-record-frequency", () => import("./training_record_frequency_controller"))
lazyLoadController(application, "ts--select", () => import("./ts/select_controller"))
lazyLoadController(application, "vehicles", () => import("./vehicles_controller"))
